import { useEffect, useState } from "react";
import axiosInstance from "../utils/axiosConfig";
import {
  Card,
  Table,
  Input,
  Button,
  Space,
  Modal,
  Form,
  Select,
  Typography,
  Spin,
  Alert,
  App,
  Tag,
  Grid,
} from "antd";
import {
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import DashboardLayout from "./DashboardLayout";

const { Title } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

const ExistingUser = () => {
  const { message } = App.useApp();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [editUser, setEditUser] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [apis, setApis] = useState([]);
  const [environment, setEnvironment] = useState("preprod"); // "prod" or "preprod"
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  // Environment URLs for user endpoints
  const environmentUrls = {
    prod: "/api/prod/user/users",
    preprod: "/api/preprod/user/users",
  };

  useEffect(() => {
    fetchUsers();
    fetchApis();
  }, [environment]); // Refetch when environment changes

  const fetchApis = async () => {
    try {
      const response = await axiosInstance.get("/api/apiCollection");
      setApis(response.data);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch API collection"
      );
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      // Use environment-specific endpoint
      const endpoint = environmentUrls[environment] || "/api/user/users";
      const response = await axiosInstance.get(endpoint);
      if (response.status === 200) {
        setUsers(response.data);
        setError("");
      } else {
        const errorMsg =
          response.data?.error ||
          response.data?.message ||
          "Failed to fetch users";
        setError(errorMsg);
        message.error(errorMsg, 4);
      }
    } catch (err) {
      const errorMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch users";
      setError(errorMsg);
      message.error(errorMsg, 4);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(`/api/user/users/${id}`);
      message.success("User deleted successfully!", 3);
      setUsers((prev) => prev.filter((user) => user.id !== id));
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Failed to delete user";
      message.error(errorMessage, 4);
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleEditSubmit = async (values) => {
    try {
      await axiosInstance.put(`/api/user/users/${editUser.id}`, {
        name: values.name,
        email: values.email,
        ApiCollectionNames: values.ApiCollections || [],
      });
      message.success("User updated successfully!");
      await fetchUsers();
      setEditUser(null);
      editForm.resetFields();
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Error updating user";
      message.error(errorMessage);
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      (user.username?.toLowerCase() || "").includes(search.toLowerCase()) ||
      (user.email?.toLowerCase() || "").includes(search.toLowerCase()) ||
      (user.apiKey?.toLowerCase() || "").includes(search.toLowerCase())
  );

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      sorter: (a, b) => a.id - b.id,
      width: 80,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      sorter: (a, b) => (a.username || "").localeCompare(b.username || ""),
    },
    {
      title: "User Role",
      dataIndex: "user_role",
      key: "user_role",
      sorter: (a, b) => (a.user_role || "").localeCompare(b.user_role || ""),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => (a.email || "").localeCompare(b.email || ""),
    },
    {
      title: "API Key",
      dataIndex: "apiKey",
      key: "apiKey",
      ellipsis: true,
      render: (text) => (
        <Typography.Text code style={{ fontSize: "12px" }}>
          {text}
        </Typography.Text>
      ),
    },
    {
      title: "API Access",
      key: "apiAccess",
      render: (_, user) => (
        <Space wrap>
          {Array.isArray(user.ApiCollections) &&
            user.ApiCollections.map((api) => (
              <Tag key={api.id || api.name} color="blue">
                {api.name}
              </Tag>
            ))}
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 180,
      render: (_, user) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => {
              setEditUser(user);
              editForm.setFieldsValue({
                name: user.name || "",
                email: user.email || "",
                ApiCollections:
                  user.ApiCollections?.map((api) => api.name) || [],
              });
            }}
          >
            Edit
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => setConfirmDelete(user.id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <DashboardLayout title="User Management">
      <Card
        style={{
          borderRadius: 12,
          boxShadow: "0 4px 16px rgba(0, 102, 255, 0.08)",
          border: "1px solid #e1e8f0",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
          <Title
            level={2}
            style={{
              margin: 0,
              background: "linear-gradient(135deg, #0066ff 0%, #0052cc 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              fontWeight: 700,
            }}
          >
            Existing Users
          </Title>
          <Select
            value={environment}
            onChange={(value) => {
              setEnvironment(value);
              setUsers([]); // Clear users while fetching
            }}
            style={{ width: 140 }}
            suffixIcon={<GlobalOutlined />}
            size="large"
          >
            <Option value="preprod">Preprod</Option>
            <Option value="prod">Production</Option>
          </Select>
        </div>

        <Input
          placeholder="Search by username, email or API key"
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ marginBottom: "24px", maxWidth: "400px" }}
          allowClear
          size="large"
        />

        {error && (
          <Alert
            title="Error"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError("")}
            style={{ marginBottom: "24px", borderRadius: 8 }}
          />
        )}

        <Spin spinning={loading}>
          <Table
            dataSource={filteredUsers}
            columns={columns}
            rowKey="id"
            pagination={{
              // defaultPageSize (not pageSize) so the size changer applies instead of
              // snapping back to a pinned value.
              defaultPageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} users`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            scroll={{ x: "max-content" }}
          />
        </Spin>

        <Modal
          title={
            <span
              style={{
                fontSize: "20px",
                fontWeight: 700,
                background: "linear-gradient(135deg, #0066ff 0%, #0052cc 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Edit User
            </span>
          }
          open={!!editUser}
          onCancel={() => {
            setEditUser(null);
            editForm.resetFields();
          }}
          footer={null}
          width={isMobile ? "92vw" : 600}
          style={{ borderRadius: 16, top: isMobile ? 16 : undefined }}
        >
          <Form form={editForm} layout="vertical" onFinish={handleEditSubmit}>
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: "Please input name!" }]}
            >
              <Input placeholder="User name" size="large" />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: "Please input email!" },
                { type: "email", message: "Please enter a valid email!" },
              ]}
            >
              <Input placeholder="Email" size="large" />
            </Form.Item>

            <Form.Item name="ApiCollections" label="API Collections">
              <Select
                mode="multiple"
                placeholder="Select API Collections"
                allowClear
                size="large"
              >
                {apis.map((api) => (
                  <Option key={api.id} value={api.name}>
                    {api.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  style={{
                    background:
                      "linear-gradient(135deg, #0066ff 0%, #0052cc 100%)",
                    border: "none",
                    fontWeight: 600,
                  }}
                >
                  Save
                </Button>
                <Button
                  onClick={() => {
                    setEditUser(null);
                    editForm.resetFields();
                  }}
                  size="large"
                >
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title={
            <span
              style={{
                fontSize: "20px",
                fontWeight: 700,
                color: "#ff3366",
              }}
            >
              Confirm Deletion
            </span>
          }
          open={!!confirmDelete}
          onOk={() => handleDelete(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
          okText="Delete"
          okButtonProps={{ danger: true, size: "large" }}
          cancelButtonProps={{ size: "large" }}
          width={isMobile ? "92vw" : 520}
          style={{ borderRadius: 16, top: isMobile ? 16 : undefined }}
        >
          <p style={{ fontSize: "14px", color: "#6b7a99" }}>
            Are you sure you want to delete this user? This action cannot be
            undone.
          </p>
        </Modal>
      </Card>
    </DashboardLayout>
  );
};

export default ExistingUser;
